import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import keycloak from "../auth/keycloak";

type JsonListener = (payload: any) => void;

const deriveWsUrl = (): string => {
  const api = process.env.REACT_APP_API_BASE_URL || "";
  return api.replace(/^http/, "ws").replace(/\/api\/v1\/?$/, "") + "/ws";
};

const WS_URL = process.env.REACT_APP_WS_URL || deriveWsUrl();

type Entry = {
  listeners: Set<JsonListener>;
  stompSub: StompSubscription | null;
};

let client: Client | null = null;
const entries = new Map<string, Entry>();
const connectListeners = new Set<() => void>();

const dispatch = (entry: Entry, msg: IMessage) => {
  try {
    const payload = JSON.parse(msg.body);
    entry.listeners.forEach((l) => l(payload));
  } catch {
    /* ignore malformed */
  }
};

const subscribeEntry = (c: Client, destination: string, entry: Entry) => {
  entry.stompSub = c.subscribe(destination, (msg) => dispatch(entry, msg));
};

const createClient = (): Client => {
  const c = new Client({
    brokerURL: WS_URL,
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    beforeConnect: async () => {
      for (let i = 0; i < 50 && (!keycloak.authenticated || !keycloak.token); i++) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      try {
        await keycloak.updateToken(30);
      } catch {}
      c.connectHeaders = { Authorization: `Bearer ${keycloak.token ?? ""}` };
    },
    onConnect: () => {
      entries.forEach((entry, destination) => subscribeEntry(c, destination, entry));
      connectListeners.forEach((l) => l());
    },
  });
  return c;
};

export const isStompConnected = () => !!client?.connected;

// Gọi mỗi lần connect thành công
export const addStompConnectListener = (listener: () => void): (() => void) => {
  connectListeners.add(listener);
  return () => {
    connectListeners.delete(listener);
  };
};

// Tự connect khi có subscription đầu tiên
export const subscribeStomp = <T>(destination: string, listener: (payload: T) => void): (() => void) => {
  let entry = entries.get(destination);
  if (!entry) {
    entry = { listeners: new Set(), stompSub: null };
    entries.set(destination, entry);
    if (client?.connected) subscribeEntry(client, destination, entry);
  }
  entry.listeners.add(listener);

  if (!client) {
    client = createClient();
    client.activate();
  }

  return () => {
    const e = entries.get(destination);
    if (!e) return;
    e.listeners.delete(listener);
    if (e.listeners.size > 0) return;
    entries.delete(destination);
    if (client?.connected && e.stompSub) e.stompSub.unsubscribe();
    if (entries.size === 0 && client) {
      client.deactivate();
      client = null;
    }
  };
};

// Ngắt hẳn socket + xoá toàn bộ subscription/listener. Gọi khi logout để tránh
export const disconnectStomp = (): void => {
  entries.clear();
  connectListeners.clear();
  if (client) {
    client.deactivate();
    client = null;
  }
};

// Reconnect để áp token mới sau khi refresh (onConnect tự subscribe lại toàn bộ entries).
export const reconnectStomp = async (): Promise<void> => {
  if (!client || entries.size === 0) return;
  await client.deactivate();
  client.activate();
};
